import React, { useRef, useState } from 'react'
import { useDndMonitor } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import Stack from '@mui/material/Stack'
import { TppCell, TppColumn, TppSlot as TppSlotType, DRAG_TYPES } from '../types'
import { useLayoutConstants } from '../hooks/useLayoutConstants'
import type { LayoutConstants } from '../hooks/useLayoutConstants'
import TppGridCell from './TppGridCell'
import TppComparisonCell from './TppComparisonCell'
import TppContextCell from './TppContextCell'
import { ColumnVersionFull } from '../../TPPChangeLog/types'
import SvgIcon from '@mui/material/SvgIcon'
import { ReactComponent as ReorderIcon } from '../../../assets/images/actions/reorder.svg'

const getStickyStyle = (colId: string, pinnedIds: string[], layout: LayoutConstants): React.CSSProperties => {
    return {} // @TODO - would be handy for comparison mode
    // if (!pinnedIds.includes(colId)) return {}

    const index = pinnedIds.indexOf(colId)
    // Left offset = RowHeader (220) + Handle (32) + Previous Pinned Cols
    const leftOffset = layout.SCROLL_START + index * layout.COLUMN_WIDTH

    return {
        position: 'sticky',
        left: leftOffset,
        zIndex: 25,
        borderRight: '2px solid',
        boxShadow: '4px 0 8px -4px rgba(0,0,0,0.1)',
        borderColor: 'figma.primary',
    }
}

interface TppSlotProps {
    slotId: string
    rowId: string
    slot?: TppSlotType
    columnIds: string[]
    cellLookup: Record<string, Record<string, TppCell>>
    pinnedIds: string[]
    showOnlyHighlighted?: boolean
    isSummary: boolean
    activeDragType: string | null
    activeCellSlotId: string | null
    visibleColumns: { id: string; label: string }[]
    columnsById: Record<string, TppColumn>
    cellsById: Record<string, TppCell>
    creatingCellKey: string | null
    onCreateCell: (slotId: string, columnId: string) => Promise<void>
    movingCellId: string | null
    movingSlotId: string | null
    showContext: boolean
    onUpdateSlotContext: (slotId: string, context: string) => Promise<void>
    isLastSlot?: boolean
    comparisonData?: ColumnVersionFull | null
    isArchivedTab?: boolean
    canEdit?: boolean
}

const TppSlot: React.FC<TppSlotProps> = ({
    slotId,
    rowId,
    slot,
    columnIds,
    cellLookup,
    pinnedIds,
    showOnlyHighlighted,
    isSummary,
    activeDragType,
    activeCellSlotId,
    visibleColumns,
    columnsById,
    cellsById,
    creatingCellKey,
    onCreateCell,
    movingCellId,
    movingSlotId,
    showContext,
    onUpdateSlotContext,
    isLastSlot,
    comparisonData,
    isArchivedTab,
    canEdit = true,
}) => {
    const layout = useLayoutConstants()
    const [isHovered, setIsHovered] = useState(false)
    const [isDragOver, setIsDragOver] = useState(false)
    const isDragOverRef = useRef(false)
    const slotCells = cellLookup[slotId] || {}

    useDndMonitor({
        onDragOver({ active, over }) {
            if (active.data.current?.type !== DRAG_TYPES.CELL) return
            if (activeDragType !== DRAG_TYPES.CELL) return

            // Check if over this slot or child
            const isOverThisSlot = over?.data.current?.slotId === slotId || over?.id === slotId
            let isValid = isOverThisSlot
            const targetColumnId = over?.data.current?.columnId
            if (targetColumnId && cellLookup[slotId]?.[targetColumnId]) {
                isValid = false
            }
            if (isDragOverRef.current !== isValid) {
                isDragOverRef.current = isValid
                setIsDragOver(isValid)
            }
        },
        onDragEnd() {
            if (isDragOverRef.current) {
                isDragOverRef.current = false
                setIsDragOver(false)
            }
        },
    })

    const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
        id: slotId,
        disabled: isSummary || movingSlotId === slotId || isArchivedTab || !canEdit,
        data: { type: DRAG_TYPES.SLOT, rowId, slotId },
    })

    let bg = 'figma.grayscale.contentWrapper'
    if (isDragging) bg = 'figma.grayscale.tier6'
    else if (isDragOver) bg = 'figma.grayscale.tier6'
    else if (isHovered) bg = 'figma.primary5'

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        width: 'max-content',
        bgcolor: bg,
        transition: 'background 0.2s ease',
        zIndex: isDragging ? 29 : 'auto',
        borderBottom: isLastSlot ? 'none' : '1px solid',
        borderColor: 'figma.grayscale.stroke',
    }

    return (
        <Stack
            direction="row"
            height="100%"
            minHeight="max-content"
            ref={setNodeRef}
            sx={style}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Stack
                direction="row"
                {...attributes}
                {...listeners}
                sx={{
                    position: 'sticky',
                    left: layout.ROW_HEADER_WIDTH,
                    zIndex: 30,
                    width: layout.SLOT_HANDLE_WIDTH,
                    minWidth: layout.SLOT_HANDLE_WIDTH,
                    bgcolor: 'figma.grayscale.contentWrapper',
                    borderRight: '1px solid',
                    borderColor: 'figma.grayscale.stroke',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isArchivedTab || isSummary || !canEdit ? 'not-allowed' : 'grab',
                    color: isDragging ? 'figma.primary' : 'figma.grayscale.tier3',
                    '&:hover': {
                        color: isArchivedTab || isSummary || !canEdit ? 'figma.grayscale.tier3' : 'figma.primary',
                    },
                }}
            >
                <SvgIcon
                    component={ReorderIcon}
                    inheritViewBox
                    fontSize="small"
                    color="inherit"
                    sx={{ fontSize: '16px' }}
                />
            </Stack>
            {showContext && (
                <TppContextCell
                    slotId={slotId}
                    context={slot?.context}
                    onUpdateContext={onUpdateSlotContext}
                    canEdit={canEdit}
                />
            )}
            {comparisonData && (
                <TppComparisonCell slotId={slotId} comparisonData={comparisonData} showContext={showContext} />
            )}

            {columnIds.map((columnId) => {
                const cell = slotCells[columnId]
                const stickyStyle = getStickyStyle(columnId, pinnedIds, layout)
                return (
                    <TppGridCell
                        key={columnId}
                        slotId={slotId}
                        columnId={columnId}
                        cell={cell}
                        stickyStyle={stickyStyle}
                        isSlotHovered={isHovered}
                        showOnlyHighlighted={showOnlyHighlighted}
                        isSummary={isSummary}
                        activeDragType={activeDragType}
                        activeCellSlotId={activeCellSlotId}
                        visibleColumns={visibleColumns}
                        columnsById={columnsById}
                        cellsById={cellsById}
                        slotCells={slotCells}
                        slotDragAttributes={attributes}
                        slotDragListeners={listeners}
                        creatingCellKey={creatingCellKey}
                        onCreateCell={onCreateCell}
                        movingCellId={movingCellId}
                        canEdit={canEdit}
                    />
                )
            })}
        </Stack>
    )
}

export default TppSlot
